/**
 *
 * Copyright 2020-2026 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import { diag } from '@opentelemetry/api'
import { ExportResult, ExportResultCode } from '@opentelemetry/core'
import {
	defaultStatusCodeTagName,
	defaultStatusErrorTagName,
	toZipkinSpan,
} from '@opentelemetry/exporter-zipkin/build/src/transform.js'
import { ReadableSpan, SpanExporter } from '@opentelemetry/sdk-trace-base'

import { hasToString } from '../types'
import { limitLen } from '../utils'
import {
	NATIVE_BEACON_SENDER,
	NATIVE_XHR_SENDER,
	NOOP_ATTRIBUTES_TRANSFORMER,
	type SplunkExporterConfig,
} from './common'

const MAX_VALUE_LIMIT = 4096
const SERVICE_NAME = 'browser'

// TODO: upstream proper exports from ZipkinExporter
export interface ZipkinAnnotation {
	timestamp: number
	value: string
}

// TODO: upstream proper exports from ZipkinExporter
export interface ZipkinEndpoint {
	ipv4?: string
	port?: number
	serviceName?: string
}

// TODO: upstream proper exports from ZipkinExporter
export interface ZipkinTags {
	[tagKey: string]: unknown
}

// TODO: upstream proper exports from ZipkinExporter
export interface ZipkinSpan {
	annotations?: ZipkinAnnotation[]
	debug?: boolean
	duration: number
	id: string
	kind?: 'CLIENT' | 'SERVER' | 'CONSUMER' | 'PRODUCER'
	localEndpoint?: ZipkinEndpoint
	name: string
	parentId?: string
	shared?: boolean
	tags: ZipkinTags
	timestamp: number
	traceId: string
}

/**
 * SplunkExporter is based on Zipkin V2. It includes Splunk-specific modifications.
 */
export class SplunkZipkinExporter implements SpanExporter {
	// TODO: a test which relies on beaconUrl needs to be fixed first
	public readonly beaconUrl: string

	private readonly _beaconSender: SplunkExporterConfig['beaconSender']

	private readonly _onAttributesSerializing: SplunkExporterConfig['onAttributesSerializing']

	private readonly _xhrSender: SplunkExporterConfig['xhrSender']

	constructor({
		beaconSender = NATIVE_BEACON_SENDER,
		onAttributesSerializing = NOOP_ATTRIBUTES_TRANSFORMER,
		url,
		xhrSender = NATIVE_XHR_SENDER,
	}: SplunkExporterConfig) {
		this.beaconUrl = url
		this._onAttributesSerializing = onAttributesSerializing
		this._xhrSender = xhrSender
		this._beaconSender = beaconSender
	}

	export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
		const spansLength = spans.length
		spans = spans.filter((span) => Boolean(span.attributes['splunk.rumSessionId']))

		if (spans.length !== spansLength) {
			diag.debug(
				'Some spans were dropped because they are missing the splunk.rumSessionId attribute. This usually means the session has reached its maximum allowed duration.',
			)
		}

		if (spans.length === 0) {
			resultCallback({ code: ExportResultCode.SUCCESS })
			return
		}

		const zspans = spans.map((span) => this._mapToZipkinSpan(span))
		const zJson = JSON.stringify(zspans)
		if (document.hidden && this._beaconSender && zJson.length <= 64_000) {
			this._beaconSender(this.beaconUrl, zJson)
		} else if (this._xhrSender) {
			this._xhrSender(this.beaconUrl, zJson, {
				'Accept': '*/*',
				'Content-Type': 'text/plain;charset=UTF-8',
			})
		}

		resultCallback({ code: ExportResultCode.SUCCESS })
	}

	shutdown(): Promise<void> {
		return Promise.resolve()
	}

	private _mapToZipkinSpan(span: ReadableSpan): ZipkinSpan {
		const preparedSpan = this._preTranslateSpan(span)
		const zspan = toZipkinSpan(preparedSpan, SERVICE_NAME, defaultStatusCodeTagName, defaultStatusErrorTagName)
		return this._postTranslateSpan(zspan)
	}

	private _postTranslateSpan(span: ZipkinSpan) {
		delete span.localEndpoint
		span.name = limitLen(span.name, MAX_VALUE_LIMIT)
		for (const [key, value] of Object.entries(span.tags)) {
			if (hasToString(value)) {
				span.tags[key] = limitLen(value.toString(), MAX_VALUE_LIMIT)
			}
		}
		// Remove inaccurate CORS timings
		const zero = performance.timeOrigin * 1000
		if (
			span.tags['http.url'] &&
			!(span.tags['http.url'] as string).startsWith(location.origin) &&
			span.timestamp > zero &&
			span.annotations
		) {
			span.annotations = span.annotations.filter(
				({ timestamp }) =>
					// Chrome has increased precision on timeOrigin but otel may round it
					// Due to multiple roundings and truncs it can be less than timeOrigin
					timestamp > zero,
			)
		}

		return span
	}

	private _preTranslateSpan(span: ReadableSpan): ReadableSpan {
		return {
			attributes: this._onAttributesSerializing
				? this._onAttributesSerializing(span.attributes, span)
				: span.attributes,
			droppedAttributesCount: span.droppedAttributesCount,
			droppedEventsCount: span.droppedEventsCount,
			droppedLinksCount: span.droppedLinksCount,
			duration: span.duration,
			ended: span.ended,
			endTime: span.endTime,
			events: span.events,
			instrumentationLibrary: span.instrumentationLibrary,
			kind: span.kind,
			links: span.links,
			// todo: once typescript is implemented, conform to ReadableSpan
			// note: some properties in Span are not enumerable, and as a result cannot be spread or Object.assign'ed
			name: span.name,

			parentSpanId: span.parentSpanId,
			resource: span.resource,

			spanContext: span.spanContext.bind(span),
			startTime: span.startTime,
			status: span.status,
		}
	}
}
